import React, {
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
} from "react";

import { AgGridReact } from "ag-grid-react";

import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";

const Device = () => {
  const gridRef = useRef(null);

  const SERVER_URL = process.env.REACT_APP_SERVER_URL;

  // =========================
  // API DATA
  // =========================

  const [data, setData] = useState([]);

  // Backend pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationRow, setPaginationRow] = useState(10);

  const [totalRecords, setTotalRecords] = useState(0);
  const totalPages = Math.ceil(totalRecords / paginationRow) || 1;
  const startItem =
    totalRecords === 0 ? 0 : (currentPage - 1) * paginationRow + 1;
  const endItem = Math.min(startItem + (data?.length || 0) - 1, totalRecords);

  const [loading, setLoading] = useState(false);

  // =========================
  // GET DEVICES
  // =========================

  const getData = useCallback(async () => {
    try {
      setLoading(true);

      const adminId = localStorage.getItem("adminId");
      const loggedRes = localStorage.getItem("logged_res");

      if (!adminId) {
        console.error("adminId is missing from localStorage");
        return;
      }

      if (!loggedRes || loggedRes === "undefined") {
        console.error("logged_res is missing or invalid");
        return;
      }

      const id = adminId.replace(/"/g, "");

      const BASE_URL =
        `${SERVER_URL}/api/admin/get-devices/${id}` +
        `?page=${currentPage}&limit=${paginationRow}`;

      console.log("API URL:", BASE_URL);

      const res = await fetch(BASE_URL, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${loggedRes}`,
          "Content-Type": "application/json",
        },
      });

      const result = await res.json();

      console.log("Device API response:", result);

      if (!res.ok) {
        throw new Error(result.message || "Failed to get device data");
      }

      // =========================
      // DEVICE DATA
      // =========================

      setData(result.data || []);

      // =========================
      // PAGINATION DATA
      // =========================

      if (result.pagination) {
        setTotalRecords(result.pagination.totalRecords || 0);
      } else if (result.totalCount !== undefined) {
        setTotalRecords(result.totalCount || 0);
      }

      // Cache current page only
      localStorage.setItem("cachedData", JSON.stringify(result.data || []));
    } catch (error) {
      console.error("Get devices error:", error);
    } finally {
      setLoading(false);
    }
  }, [SERVER_URL, currentPage, paginationRow]);

  // =========================
  // API CALL
  // =========================

  useEffect(() => {
    getData();
  }, [getData]);

  // =========================
  // COLUMN DEFINITIONS
  // =========================

  const columnDefs = useMemo(
    () => [
      {
        headerName: "S.No",
        field: "index",
        minWidth: 100,
        width: 100,
        sortable: false,
        filter: false,
        checkboxSelection: true,
        headerCheckboxSelection: true,
        showDisabledCheckboxes: true,
      },

      {
        headerName: "ID",
        field: "id",
        minWidth: 100,
        sortable: true,
        filter: true,
      },

      {
        headerName: "Unique ID",
        field: "unique_id",
        minWidth: 300,
        sortable: true,
        filter: true,
      },

      {
        headerName: "Band ID",
        field: "band_id",
        minWidth: 220,
        sortable: true,
        filter: true,

        cellRenderer: (params) => (params.value ? params.value : "-"),
      },

      // {
      //   headerName: "BLE ID",
      //   field: "ble_id",
      //   minWidth: 220,
      //   sortable: true,
      //   filter: true,

      //   cellRenderer: (params) => (params.value ? params.value : "-"),
      // },

      {
        headerName: "Created At",
        field: "created_at",
        minWidth: 200,
        sortable: true,
        filter: true,

        cellRenderer: (params) => (params.value ? params.value : "-"),
      },

      {
        headerName: "Updated At",
        field: "updated_at",
        minWidth: 200,
        sortable: true,
        filter: true,

        cellRenderer: (params) => (params.value ? params.value : "-"),
      },

      {
        headerName: "Status",
        field: "is_active",
        minWidth: 150,
        flex: 1,
        sortable: true,
        filter: true,

        cellRenderer: (params) => {
          return params.value ? (
            <span className="badge badge-success">Active</span>
          ) : (
            <span className="badge badge-danger">Inactive</span>
          );
        },
      },

      // {
      //   headerName: "On / Off",
      //   field: "is_active",
      //   minWidth: 150,
      //   sortable: false,
      //   filter: false,

      //   cellRenderer: (params) => {
      //     return (
      //       <div
      //         style={{
      //           textAlign: "center",
      //           padding: "8px",
      //         }}
      //       >
      //         <input
      //           type="checkbox"
      //           id={`toggle-${params.data.id}`}
      //           className="toggle-input"
      //           checked={Boolean(params.value)}
      //           onChange={() => {
      //             console.log("Toggle device:", params.data.id);
      //           }}
      //         />

      //         <label
      //           htmlFor={`toggle-${params.data.id}`}
      //           className="toggle-label"
      //         ></label>
      //       </div>
      //     );
      //   },
      // },
    ],
    [],
  );

  // =========================
  // DEFAULT COLUMN
  // =========================

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      filter: true,

      filterParams: {
        buttons: ["reset", "apply"],
      },

      floatingFilter: true,
      resizable: true,
    }),
    [],
  );

  // =========================
  // ADD SERIAL NUMBER
  // =========================

  const rowData = useMemo(() => {
    return data.map((row, index) => ({
      ...row,

      // Example:
      // Page 1 => 1-10
      // Page 2 => 11-20
      index: (currentPage - 1) * paginationRow + index + 1,
    }));
  }, [data, currentPage, paginationRow]);

  // =========================
  // SELECTION
  // =========================

  const [selectedData, setSelectedData] = useState([]);

  const onSelectionChanged = useCallback((event) => {
    const selectedRows = event.api.getSelectedRows();

    console.log("Selected devices:", selectedRows);

    setSelectedData(selectedRows);
  }, []);

  // =========================
  // PAGE CHANGE
  // =========================

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > totalPages) {
      return;
    }

    if (gridRef.current?.api) {
      gridRef.current.api.showLoadingOverlay();
    }

    setCurrentPage(newPage);
  };

  // =========================
  // CHANGE PAGE SIZE
  // =========================

  const handlePageSizeChange = (event) => {
    const newLimit = Number(event.target.value);

    setPaginationRow(newLimit);

    if (gridRef.current?.api) {
      gridRef.current.api.showLoadingOverlay();
    }

    // When page size changes,
    // start from page 1
    setCurrentPage(1);
  };

  // =========================
  // REFRESH
  // =========================

  const handleButtonClick = () => {
    getData();
  };

  // =========================
  // EXPORT
  // =========================

  const onBtnExport = useCallback(() => {
    if (gridRef.current) {
      gridRef.current.api.exportDataAsCsv();
    }
  }, []);

  // =========================
  // UI
  // =========================

  return (
    <div className="content-wrapper">
      <div className="row mt-3">
        <div className="col-md-12 grid-margin stretch-card">
          <div className="card">
            <div className="card-body">
              {/* =========================
                  HEADER
              ========================= */}

              <div className="d-flex manageLinkTable justify-content-between align-items-center mb-3">
                <h4 className="table_title card-title mb-1">
                  Device
                  <span>
                    <button
                      className="btn btn-sm bg-white btn-icon-text border"
                      onClick={handleButtonClick}
                      disabled={loading}
                    >
                      <i
                        className="typcn typcn-refresh"
                        style={{
                          height: "4px",
                          width: "4px",
                        }}
                      ></i>
                    </button>
                  </span>
                </h4>

                <div className="manageTablesOptions">
                  <div>
                    <select
                      className="dropdown setpageSelection show form-control"
                      name="pagination"
                      style={{
                        padding: ".5rem .75rem",
                        borderRadius: "4px",
                      }}
                      value={paginationRow}
                      onChange={handlePageSizeChange}
                    >
                      <option value={10}>show 10 rows</option>
                      <option value={20}>show 20 rows</option>
                      <option value={25}>show 25 rows</option>
                      <option value={50}>show 50 rows</option>
                      <option value={100}>show 100 rows</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* =========================
                  GRID
              ========================= */}

              <div
                className="ag-theme-alpine"
                style={{
                  width: "100%",
                  height: 620,
                }}
              >
                <AgGridReact
                  ref={gridRef}
                  rowData={rowData}
                  columnDefs={columnDefs}
                  defaultColDef={defaultColDef}
                  /*
                   * IMPORTANT:
                   * Backend pagination is being used.
                   * Therefore AG Grid pagination is disabled.
                   */
                  pagination={false}
                  enableCellTextSelection={true}
                  rowSelection="multiple"
                  suppressRowClickSelection={true}
                  onSelectionChanged={onSelectionChanged}
                  rowHeight={50}
                  animateRows={true}
                  overlayLoadingTemplate={
                    '<span class="ag-overlay-loading-center">Loading...</span>'
                  }
                />
              </div>

              {/* =========================
                  PAGINATION
              ========================= */}

              <div className="row">
                <div className="col-12">
                  <div className="table-responsive">
                    <div
                      id="order-listing_wrapper"
                      className="dataTables_wrapper dt-bootstrap4 no-footer"
                    >
                      <div className="row">
                        <div className="col-sm-12 col-md-5">
                          <div
                            className="dataTables_info"
                            id="order-listing_info"
                            role="status"
                            aria-live="polite"
                          >
                            Showing {startItem} to {endItem} of {totalRecords}{" "}
                            entries{" "}
                          </div>
                        </div>
                        <div className="col-sm-12 col-md-7">
                          <div
                            className="dataTables_paginate paging_simple_numbers"
                            id="order-listing_paginate"
                          >
                            <ul className="pagination">
                              <li
                                className={`paginate_button page-item ${
                                  currentPage === 1 ? "disabled" : ""
                                }`}
                                id="order-listing_previous"
                                onClick={() => {
                                  if (currentPage !== 1) {
                                    handlePageChange(1);
                                  }
                                }}
                                disabled={currentPage === 1}
                              >
                                <button
                                  aria-controls="order-listing"
                                  data-dt-idx="0"
                                  tabIndex="0"
                                  className="page-link"
                                >
                                  <i
                                    className="fa fa-angle-double-left"
                                    style={{
                                      height: "4px",
                                      width: "4px",
                                    }}
                                  ></i>
                                </button>
                              </li>
                              <li
                                className={`paginate_button page-item ${
                                  currentPage === 1 ? "disabled" : ""
                                }`}
                                id="order-listing_previous"
                                onClick={() => {
                                  if (currentPage !== 1) {
                                    handlePageChange(currentPage - 1);
                                  }
                                }}
                                disabled={currentPage === 1}
                              >
                                <button
                                  aria-controls="order-listing"
                                  data-dt-idx="0"
                                  tabIndex="0"
                                  className="page-link"
                                >
                                  <i
                                    className="fa fa-chevron-left"
                                    style={{
                                      height: "4px",
                                      width: "4px",
                                    }}
                                  ></i>
                                </button>
                              </li>

                              <li className="paginate_button page-item active">
                                <p
                                  aria-controls="order-listing"
                                  className="page-link"
                                >
                                  Page {currentPage} of {totalPages}
                                </p>
                              </li>

                              <li
                                className={`paginate_button page-item ${
                                  currentPage * paginationRow >= totalRecords
                                    ? "disabled"
                                    : ""
                                }`}
                                id="order-listing_next"
                                onClick={() => {
                                  if (
                                    currentPage * paginationRow < totalRecords
                                  ) {
                                    handlePageChange(currentPage + 1);
                                  }
                                }}
                              >
                                <button
                                  aria-controls="order-listing"
                                  data-dt-idx="2"
                                  tabIndex="0"
                                  className="page-link"
                                >
                                  <i
                                    className="fa fa-chevron-right"
                                    style={{ height: "4px", width: "4px" }}
                                  ></i>
                                </button>
                              </li>
                              <li
                                className={`paginate_button page-item ${
                                  currentPage * paginationRow >= totalRecords
                                    ? "disabled"
                                    : ""
                                }`}
                                id="order-listing_next"
                                onClick={() => {
                                  if (currentPage !== totalPages) {
                                    handlePageChange(totalPages);
                                  }
                                }}
                              >
                                <button
                                  aria-controls="order-listing"
                                  data-dt-idx="2"
                                  tabIndex="0"
                                  className="page-link"
                                >
                                  <i
                                    className="fa fa-angle-double-right"
                                    style={{ height: "4px", width: "4px" }}
                                  ></i>
                                </button>
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Device;
